import ErrorIcon from '@mui/icons-material/Error';
import { Box, Typography, Tooltip } from '@mui/material';
import { SWToolTip } from 'components/SWToolTip';
import { ThemePalette } from 'mui.theme';
import {
  convertCentToFahr,
  convertKpaToPsi,
  getLabelWrtUnitSystem,
} from 'utils/helpers/unitSystem';
import { fixedLimts } from 'views/TirePressure/util';
import { Guage } from './Guage';
import { BatteryChargingFull } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  value: number;
  limit: any;
  tireCode: string;
  unitSystem: string;
  tireCodes: any;
  metricType: string;
  slowLeaks: string[];
  fastLeaks: string[];
  isAlert: boolean;
  fitmentDate: string;
  hasSensor: boolean;
  isBatteryLow?: boolean;
  serialNumber?: string;
  lastUpdated?: string;
}

const TirePressure = ({
  enableDarkTheme,
  value,
  tireCode,
  unitSystem,
  tireCodes,
  metricType,
  slowLeaks,
  fastLeaks,
  limit,
  isAlert,
  fitmentDate,
  hasSensor,
  isBatteryLow,
  serialNumber,
  lastUpdated,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const lowerCaseMetricType = metricType.toLowerCase();
  // @ts-expect-error ignore this we have it defined
  const limitsData = fixedLimts[unitSystem][lowerCaseMetricType];

  // default keeping as 0 and 100
  let maxValue: number =
    metricType === 'Pressure'
      ? limit?.maxPressure
      : metricType === 'Temperature'
      ? limit?.maxTemperature
      : 100;
  let minValue: number =
    metricType === 'Pressure'
      ? limit?.minPressure
      : metricType === 'Temperature'
      ? limit?.minTemperature
      : 0;
  if (unitSystem === 'imperial') {
    if (metricType === 'Pressure') {
      maxValue = convertKpaToPsi(maxValue);
      minValue = convertKpaToPsi(minValue);
    } else if (metricType === 'Temperature') {
      maxValue = convertCentToFahr(maxValue);
      minValue = convertCentToFahr(minValue);
    }
  }

  // getting start and end range for gauge chart from config
  const startRange = limitsData?.startValue;
  const endRange: number = limitsData?.endValue;

  const showLeaksNotification = metricType === 'Pressure';
  const hasSlowLeaks = slowLeaks.includes(tireCode);
  const hasFastLeaks = fastLeaks.includes(tireCode);

  const noOfNotifications = (hasSlowLeaks ? 1 : 0) + (hasFastLeaks ? 1 : 0);

  const toolTipTitle = (() => {
    if (hasSlowLeaks && hasFastLeaks) {
      return 'Fast Leak detected\nSlow Leak detected';
    } else if (hasSlowLeaks) {
      return 'Slow Leak detected';
    } else if (hasFastLeaks) {
      return 'Fast Leak detected';
    } else {
      return 'No Leaks detected';
    }
  })();

  return (
    <Box
      sx={{
        padding: '10px',
        paddingLeft: '15px',
        paddingRight: '15px',
        border: `1px solid ${palette.cardBorderColor}`,
        borderRadius: '5px',
        marginBottom: '20px',
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.boxBackground,
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '20px',
          marginBottom: '20px',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
            fontWeight: '700',
          }}
        >
          {
            tireCodes[
              '0x' + tireCode.toLowerCase().replace('0x', '').toUpperCase()
            ]
          }
        </Typography>
        {isBatteryLow && (
          <SWToolTip title={t('low_battery')} placement="top">
            <BatteryChargingFull
              sx={{
                color: ThemePalette.typography.danger,
                fontSize: '28px',
                animation: 'blink 1s infinite',
                '@keyframes blink': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          </SWToolTip>
        )}
      </Box>
      <Typography
        sx={{
          fontSize: '10px',
          color: enableDarkTheme
            ? ThemePalette.typography.wheat
            : ThemePalette.typography.black,
        }}
      >
        {t('fitment_date')} {fitmentDate}
      </Typography>
      {serialNumber && (
        <Tooltip title={`Serial Number: ${serialNumber}`} placement="top">
          <Typography
            sx={{
              fontSize: '10px',
              color: enableDarkTheme
                ? ThemePalette.typography.wheat
                : ThemePalette.typography.black,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              cursor: 'help',
            }}
          >
            {t('serial_number')} {serialNumber}
          </Typography>
        </Tooltip>
      )}
      <Box
        sx={{
          paddingTop: '20px',
        }}
      >
        <Guage
          enableDarkTheme={enableDarkTheme}
          value={value}
          maxValue={maxValue}
          minValue={minValue}
          unitSystem={unitSystem}
          metricType={metricType}
          startRange={startRange}
          endRange={endRange}
        />
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: '700',
            paddingBottom: '10px',
            textAlign: 'center',
            color:
              value === null || value === undefined
                ? ThemePalette.typography.grey
                : !isAlert
                ? ThemePalette.typography.success
                : ThemePalette.typography.danger,
            marginTop: '20px',
          }}
        >
          {value === null || value === undefined ? (
            <SWToolTip
              title={
                hasSensor
                  ? 'TPMS data is not available'
                  : 'TPMS sensor is not installed'
              }
              placement="top"
            >
              <span
                style={{
                  fontSize: '14px',
                  cursor: 'help',
                }}
              >
                {hasSensor ? 'Data N/A' : 'Sensor N/A'}
              </span>
            </SWToolTip>
          ) : (
            <>
              {Math.round(value)}{' '}
              <span
                style={{
                  color: ThemePalette.typography.lightGrey,
                  fontSize: '14px',
                  fontWeight: 'normal',
                }}
              >
                {metricType === 'Pressure'
                  ? unitSystem === 'metric'
                    ? 'KPA'
                    : 'PSI'
                  : getLabelWrtUnitSystem('C', unitSystem).toLowerCase()}
              </span>
            </>
          )}
        </Typography>

        {lastUpdated && (
          <Typography
            sx={{
              fontSize: '10px',
              textAlign: 'center',
              color: enableDarkTheme
                ? ThemePalette.typography.wheat
                : ThemePalette.typography.black,
              marginTop: '4px',
              opacity: 0.8,
            }}
          >
            {lastUpdated}
          </Typography>
        )}

        {showLeaksNotification && noOfNotifications > 0 && (
          <SWToolTip title={toolTipTitle} placement="top-start">
            <Typography
              sx={{
                fontSize: '12px',
                textAlign: 'center',
                color:
                  noOfNotifications === 0
                    ? ThemePalette.typography.success
                    : ThemePalette.typography.danger,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {noOfNotifications > 0 && (
                <Box
                  sx={{
                    animation: 'blink 1.5s infinite',
                    '@keyframes blink': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.3 },
                      '100%': { opacity: 1 },
                    },
                  }}
                >
                  <ErrorIcon
                    sx={{
                      color: ThemePalette.typography.danger,
                      fontSize: '12px',
                      marginRight: '2px',
                    }}
                  />
                </Box>
              )}
              <span>
                {noOfNotifications} {t('notification_s')}
              </span>
            </Typography>
          </SWToolTip>
        )}
      </Box>
    </Box>
  );
};

export default TirePressure;
